import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Trophy, Mail, Phone, RefreshCw, Award } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const RANK_STYLES = {
  1: { bg: "bg-yellow-500", text: "text-yellow-50", emoji: "🥇" },
  2: { bg: "bg-gray-400", text: "text-gray-50", emoji: "🥈" },
  3: { bg: "bg-orange-700", text: "text-orange-50", emoji: "🥉" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

const formatPrice = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export const TopCustomersWidget = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTop = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/top-customers?limit=10`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setCustomers(r.data?.customers || []);
    } catch (e) {
      console.error("[top-customers] fetch failed:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTop();
  }, [fetchTop]);

  const totalRevenue = customers.reduce((s, c) => s + (c.total_spent || 0), 0);

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="top-customers-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Top 10 Clienti VIP
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchTop}
            disabled={loading}
            className="h-7 px-2 text-gray-300 hover:text-white"
            data-testid="top-customers-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
        {customers.length > 0 && (
          <p className="text-xs text-gray-400">
            Fatturato top 10:{" "}
            <span className="text-emerald-400 font-bold">{formatPrice(totalRevenue)}</span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && customers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">Caricamento...</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Nessun cliente con ordini completati
          </div>
        ) : (
          <div className="space-y-2">
            {customers.map((c, idx) => {
              const rank = idx + 1;
              const style = RANK_STYLES[rank];
              return (
                <div
                  key={c.email}
                  className="flex items-center gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700 hover:border-gray-600 transition-colors"
                  data-testid={`top-customer-row-${rank}`}
                >
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                      style ? `${style.bg} ${style.text}` : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    {style ? style.emoji : `#${rank}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <p className="text-sm font-semibold text-white truncate">
                        {c.name || c.email.split("@")[0]}
                      </p>
                      {c.referral_code && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 font-mono">
                          {c.referral_code}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5 truncate">
                      <a
                        href={`mailto:${c.email}`}
                        className="hover:text-blue-400 flex items-center gap-1 truncate"
                        data-testid={`top-customer-email-${rank}`}
                      >
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{c.email}</span>
                      </a>
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="hover:text-emerald-400 flex items-center gap-1 hidden md:flex"
                        >
                          <Phone className="w-3 h-3" />
                          {c.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-emerald-300">
                      {formatPrice(c.total_spent)}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {c.order_count} {c.order_count === 1 ? "ordine" : "ordini"} ·{" "}
                      {formatDate(c.last_order)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {customers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1.5">
              <Award className="w-3 h-3" />
              Ordina questi clienti VIP per priorità + considera offerte esclusive
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
